<template>
  <NBox class="bg-neutral-quinary p-4 mt-2 mb-4">
    <div class="flex justify-between items-center mb-2">
      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ t('option.strikePrice') }}
      </NText>

      <Number :value="position.strikePrice" #="{ number }">
        <div>
          <NText
            dir="ltr"
            variant="caption1"
            weight="medium"
            class="text-neutral-primary"
          >
            {{ number }}
          </NText>
          <NText
            variant="caption2"
            weight="medium"
            class="text-neutral-tertiary ms-1"
          >
            {{ t('misc.rial') }}
          </NText>
        </div>
      </Number>
    </div>

    <div class="flex justify-between items-center mb-3">
      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ t('option.dueDate') }}
      </NText>

      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ d(position.physicalSettlementDate as string, 'shortDate') }}
      </NText>
    </div>

    <div class="flex justify-between items-center mb-3">
      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ t('option.settlementType') }}
      </NText>

      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ SETTLEMENT_TYPE[position.settlementType] }}
      </NText>
    </div>

    <div
      v-if="position.cashSettlementQuantity"
      class="flex justify-between items-center mb-3"
    >
      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{
          `${t('misc.quantity')} ${t('option.settlemented')} ${t(
            'option.settlementRequest.cash'
          )}`
        }}
      </NText>

      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ position.cashSettlementQuantity }}
      </NText>
    </div>

    <div
      v-if="position.physicalSettlementQuantity"
      class="flex justify-between items-center mb-3"
    >
      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{
          `${t('misc.quantity')} ${t('option.settlemented')} ${t(
            'option.settlementRequest.physical'
          )}`
        }}
      </NText>

      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ position.physicalSettlementQuantity }}
      </NText>
    </div>

    <div
      v-if="position.failedToDeliverQuantity"
      class="flex justify-between items-center mb-3"
    >
      <div class="flex justify-start items-center gap-1">
        <NText variant="caption1" weight="medium" class="text-neutral-primary">
          {{ t('option.failedToDeliverQuantityTable') }}
        </NText>
        <NegarInfoWithBorderIcon
          size="16"
          class="-mt-1 cursor-pointer"
          @click="
            openFailedToDeliverInfoDialog(position.failedToDeliverQuantity)
          "
        />
      </div>

      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ position.failedToDeliverQuantity }}
      </NText>
    </div>

    <div class="flex justify-between items-center mb-2">
      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ `${t('misc.quantity')} ${t('misc.position')}` }}
      </NText>

      <NText
        :key="position.quantity"
        variant="caption2"
        weight="medium"
        class="rounded-md px-2 py-0.5"
        :class="
          isPositionBuy(position.traderDirection)
            ? 'bg-brand-secondary text-brand-primary'
            : 'bg-critical-secondary text-critical-primary'
        "
      >
        {{ position.quantity }}
      </NText>
    </div>

    <div class="flex justify-between items-center mb-2">
      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ t('modules.portfolio.net_average') }}
      </NText>

      <Number :value="position.netAveragePrice" #="{ number }" round>
        <div>
          <NText
            dir="ltr"
            variant="caption1"
            weight="medium"
            class="text-neutral-primary"
          >
            {{ number }}
          </NText>
          <NText
            variant="caption2"
            weight="medium"
            class="text-neutral-tertiary ms-1"
          >
            {{ t('misc.rial') }}
          </NText>
        </div>
      </Number>
    </div>

    <div class="flex justify-between items-center mb-2">
      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ t('option.costPrice') }}
      </NText>

      <Number :value="position.costPrice" #="{ number }" round>
        <div>
          <NText dir="ltr" variant="caption1" weight="medium">
            {{ number }}
          </NText>
          <NText
            variant="caption2"
            weight="medium"
            class="text-neutral-tertiary ms-1"
          >
            {{ t('misc.rial') }}
          </NText>
        </div>
      </Number>
    </div>

    <div class="flex justify-between items-center mb-2">
      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ t('option.retainedProfitAndLoss') }}
      </NText>

      <div class="flex items-center">
        <Number
          :value="position.returnValuePercentage / 100"
          #="{ number }"
          sign-display="exceptZero"
          number-style="percent"
          maximum-fraction-digits="2"
        >
          <NText
            dir="ltr"
            variant="caption1"
            weight="medium"
            :class="{
              'text-neutral-primary': position.returnValuePercentage === 0,
              'text-critical-primary': position.returnValuePercentage < 0,
              'text-brand-primary': position.returnValuePercentage > 0,
            }"
          >
            ({{ number }})
          </NText>
        </Number>

        <Number :value="position.returnValue" #="{ number }" round="down">
          <div>
            <NText
              dir="ltr"
              variant="caption1"
              weight="medium"
              class="me-1 text-neutral-primary"
            >
              {{ number }}
            </NText>
            <NText
              variant="caption2"
              weight="medium"
              class="text-neutral-tertiary ms-1"
            >
              {{ t('misc.rial') }}
            </NText>
          </div>
        </Number>
      </div>
    </div>

    <div class="flex justify-between items-center mb-2">
      <NText variant="caption1" weight="medium" class="text-neutral-primary">
        {{ t('option.praticesProfitAndLoss') }}
      </NText>

      <Number :value="position.strikeReturnValue" #="{ number }" round>
        <div>
          <NText
            dir="ltr"
            variant="caption1"
            weight="medium"
            class="text-neutral-primary me-1"
          >
            {{ number }}
          </NText>
          <NText
            variant="caption2"
            weight="medium"
            class="text-neutral-tertiary ms-1"
          >
            {{ t('misc.rial') }}
          </NText>
        </div>
      </Number>
    </div>
  </NBox>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import Number from '@/components/Number.vue';
import { NegarInfoWithBorderIcon } from '@negar/icons';
import { SETTLEMENT_TYPE, useOptionDialogs } from '@/modules/option';
import { isPositionBuy, PositionHistoryItemWithPrices } from '@core/option';

type TPositionInfoProps = {
  position: PositionHistoryItemWithPrices;
};

defineProps<TPositionInfoProps>();

const { t, d } = useI18n();
const { openFailedToDeliverInfoDialog } = useOptionDialogs();
</script>
